// Brand-menu hook: process image download handlers for the ACTIVE process
// tab, exporting exactly what's on screen (current layout variant from the
// URL hash, current walk incl. branch picks + expanded subprocesses, the
// app's "colour by" owner coloring). Null when there's nothing to export —
// no active process, no walk yet, or the tree layout (an outline, not a
// diagram surface) — so the menu items hide.
import { useMemo } from "react";
import type { ArchitectureModel } from "@archlang/scene/arch-model";
import { useProcessPlaybackContext } from "../workbench/ProcessPlaybackContext.js";
import type { NodeColoring } from "../board/coloring/types.js";
import { useLayoutParam } from "./useLayoutParam.js";
import { ownerColorForModel } from "./ownerColorFor.js";
import {
  buildProcessImageSvg,
  downloadProcessPng,
  downloadProcessSvg,
  processImageFilename,
  type ProcessImageVariant,
} from "./exportProcess.js";

export interface ProcessImageExport {
  readonly exportPng: () => Promise<void>;
  readonly exportSvg: () => void;
}

export function useProcessImageExport(
  model: ArchitectureModel | null,
  coloring: NodeColoring | null,
): ProcessImageExport | null {
  const { activeProcess, walkResult, walkResultAllBranches, swimlaneContext } =
    useProcessPlaybackContext();
  const { layout } = useLayoutParam();

  return useMemo(() => {
    // Same walk precedence the live views render from (useProcessLayouts).
    const walk = walkResultAllBranches ?? walkResult;
    if (!activeProcess || !walk || layout === "tree") return null;
    const variant: ProcessImageVariant = layout;
    const build = () =>
      buildProcessImageSvg({
        variant,
        walk,
        swimlaneContext,
        ownerColor:
          model && coloring ? ownerColorForModel(model, coloring) : undefined,
      });
    const name = activeProcess.name;
    return {
      exportSvg: () =>
        downloadProcessSvg(build(), processImageFilename(name, variant, "svg")),
      exportPng: () =>
        downloadProcessPng(build(), processImageFilename(name, variant, "png")),
    };
  }, [activeProcess, walkResult, walkResultAllBranches, swimlaneContext, layout, model, coloring]);
}
